import type { Router } from 'vue-router';
import { AuthenticationStatusCode } from '@Enums/authenticationStatusCode.enum';
import { AuthFlowType } from '@Enums/authFlowType.enum';
import type { CompleteLoginResponse, VerifyDeviceResponse } from '@Types/deviceAuth.interface';
import type { OtpVerifyResponse } from '@Types/otp.interface';
import { useDeviceAuthStore } from '@Stores/deviceAuth.store';
import { useAuthFlowStore } from '@Stores/authFlow.store';
import { useAuthSessionStore } from '@Stores/authSession.store';
import { clearAuthState } from '@Utils/cookieUtils';
import { redirectToSessionExpired } from '@Utils/sessionExpired';
import { verifyActivitySession } from 'host/mfeBridge';

export async function handleStatusCode(
    response: VerifyDeviceResponse | CompleteLoginResponse | OtpVerifyResponse,
    router: Router,
    advanceFlow: () => Promise<void>
): Promise<void> {
    const deviceAuthStore = useDeviceAuthStore();
    const authFlowStore = useAuthFlowStore();
    const authSessionStore = useAuthSessionStore();

    // Defensive guard: OtpVerifyResponse legitimately allows statusCode to be
    // null/undefined (legacy pre-token-exchange responses), so the type system
    // can't enforce that callers gate before invoking us. Today's view callers
    // do gate, but a future caller passing a missing statusCode would silently
    // fall through to the `default` arm with a misleading "general error"
    // message. Fail loud and visibly here instead.
    if (response.statusCode == null) {
        console.error('[Auth MFE] handleStatusCode called without a statusCode');
        deviceAuthStore.setError('An unexpected error occurred.');
        await router.push({ name: 'general-error' });
        return;
    }

    switch (response.statusCode) {
        case AuthenticationStatusCode.Success:
            await advanceFlow();
            break;

        case AuthenticationStatusCode.AccessDenied:
            clearAuthState();
            await router.push({ name: 'access-denied' });
            break;

        case AuthenticationStatusCode.StepUpRequired: {
            const flowSteps = response.flowSteps;

            if (flowSteps && flowSteps.length > 0) {
                await verifyActivitySession();
                authFlowStore._clearFlowSequence();
                authFlowStore.addToAuthFlow(flowSteps);
                await advanceFlow();
            } else {
                console.error('[Auth MFE] No flowSteps for step-up');
                deviceAuthStore.setError('Unable to proceed with verification.');
                await router.push({ name: 'general-error' });
            }
            break;
        }

        case AuthenticationStatusCode.DeviceThrottled:
            clearAuthState();
            await router.push({ name: 'device-throttled' });
            break;

        case AuthenticationStatusCode.NoActivitySession:
            // The browser's activity-session cookie points at a session the server has deleted.
            // Retrying sends the same dead cookie, so send the user to the host's
            // session-expired screen, which starts a new session.
            clearAuthState();
            redirectToSessionExpired();
            break;

        case AuthenticationStatusCode.TokenFamilyRevoked: {
            clearAuthState();
            authSessionStore.setFullLoginInitiated(true);
            authFlowStore._clearFlowSequence();
            authFlowStore.addToAuthFlow([{ stepName: AuthFlowType.VerifyPasscode }]);
            await verifyActivitySession();
            await advanceFlow();
            break;
        }

        case AuthenticationStatusCode.ManualReview:
            // Terminal: the user's OTP was correct but their identity is queued
            // for manual review. Do NOT route to 'mfa-failed' — that page says
            // "Verification limit reached" and falsely implies they entered a
            // wrong code. The 'pause' view already carries the right copy
            // ("We're reviewing your request… up to 2 hours").
            await router.push({ name: 'pause' });
            break;

        case AuthenticationStatusCode.FullLoginRequired: {
            const verifyResponse = response as VerifyDeviceResponse;
            const { availableChannels } = verifyResponse;

            // Multi-channel OTP selection: if availableChannels is present and non-empty,
            // route to channel selection view. Otherwise fall back to legacy scalar routing.
            if ((availableChannels?.length ?? 0) > 0) {
                deviceAuthStore.setAvailableChannels(availableChannels!);
                authSessionStore.setFullLoginInitiated(true);
                await verifyActivitySession();
                authFlowStore._clearFlowSequence();
                authFlowStore.addToAuthFlow([{ stepName: AuthFlowType.ChannelSelection }]);
                await router.push({ name: 'channel-selection' });
                break;
            }

            // Legacy scalar routing (flag OFF or no availableChannels)
            const { connectionType } = verifyResponse;
            deviceAuthStore.handleVerifyDeviceResponse(verifyResponse);
            authSessionStore.setFullLoginInitiated(true);
            await verifyActivitySession();
            authFlowStore._clearFlowSequence();

            const isEmailConnection = typeof connectionType === 'string'
                && connectionType.toLowerCase() === 'email';

            if (isEmailConnection) {
                // Defensive: server contract says Email should arrive as
                // EmailOtpRequired. If we somehow get here with Email, fall
                // through to the email-OTP flow rather than breaking the user.
                // Use VerifyEmailCode (single-step flow → just verify-email-code)
                // instead of VerifyEmailOtp (which would also queue the
                // verify-email send screen and expose it on back-navigation).
                authFlowStore.addToAuthFlow([{ stepName: AuthFlowType.VerifyEmailCode }]);
                authFlowStore.setCurrentRoute('verify-email-code');
                await router.push({ name: 'verify-email-code' });
                break;
            }

            authFlowStore.addToAuthFlow([{ stepName: AuthFlowType.VerifyPhoneOtp }]);
            await advanceFlow();
            break;
        }

        case AuthenticationStatusCode.EmailOtpRequired: {
            // The BFF auto-sent an email OTP. Skip the "enter your email" send
            // screen and drop the user directly on the email-OTP code-entry
            // screen. verifyActivitySession is wrapped in a try/catch because a
            // transient activity-session blip should not strand the user
            // mid-flow on a path where the OTP has already been dispatched.
            const verifyResponse = response as VerifyDeviceResponse;
            deviceAuthStore.handleVerifyDeviceResponse(verifyResponse);
            deviceAuthStore.setLastDeliveryType('Email');
            authSessionStore.setFullLoginInitiated(true);
            try {
                await verifyActivitySession();
            } catch (err) {
                console.error('[Auth MFE] verifyActivitySession failed in EmailOtpRequired handler (non-fatal, continuing):', err);
            }
            authFlowStore._clearFlowSequence();
            // Use VerifyEmailCode (single-step flow → just verify-email-code)
            // instead of VerifyEmailOtp (which would also queue the
            // verify-email send screen and expose it on back-navigation).
            authFlowStore.addToAuthFlow([{ stepName: AuthFlowType.VerifyEmailCode }]);
            // Set currentRoute first so the afterEach-triggered setCurrentRoute
            // stays consistent across the direct push.
            authFlowStore.setCurrentRoute('verify-email-code');
            await router.push({ name: 'verify-email-code' });
            break;
        }

        case AuthenticationStatusCode.DisambiguationRequired: {
            const loginResponse = response as CompleteLoginResponse;
            if (loginResponse.matchedAccounts?.length) {
                deviceAuthStore.setMatchedAccounts(loginResponse.matchedAccounts);
                router.push({ name: 'select-account' });
            } else {
                deviceAuthStore.setError('Unable to resolve your account.');
                router.push({ name: 'general-error' });
            }
            break;
        }

        case AuthenticationStatusCode.EmailOtpMissingClientId: {
            // Email-OTP invariant violation: session.ClientId was null.
            // This should never happen in normal flows — it indicates a
            // server-side or session setup issue. Log and route to generic error.
            console.warn('[EmailOtpMissingClientId] BFF invariant violation — session.ClientId was null on email OTP path');
            deviceAuthStore.setError('An unexpected error occurred.');
            router.push({ name: 'general-error' });
            break;
        }

        case AuthenticationStatusCode.InvalidAccountSelection: {
            // Recoverable: the picked (accountId, userId) isn't in the rebuilt
            // row set. Stay on the picker so the user can re-pick. If the
            // server returned a refreshed matchedAccounts, use it; otherwise
            // keep the current list and surface the error on the picker.
            const loginResponse = response as CompleteLoginResponse;
            if (loginResponse.matchedAccounts?.length) {
                deviceAuthStore.setMatchedAccounts(loginResponse.matchedAccounts);
            }
            deviceAuthStore.setError(
                'That selection is no longer valid. Please choose an account and try again.',
            );
            router.push({ name: 'select-account' });
            break;
        }

        default:
            deviceAuthStore.setError('An unexpected error occurred.');
            router.push({ name: 'general-error' });
            break;
    }
}
